/**
 * Generates a deterministic color based on a timestamp or unique identifier
 * @param identifier - A unique identifier (timestamp, ID, etc.) to generate color from
 * @returns An object with background color class and hover color class for Tailwind CSS
 */
export function getIssueColorByIdentifier(identifier: string | number): {
  bg: string;
  hover: string;
  bgClass: string;
  hoverClass: string;
} {
  // Convert identifier to string and create hash
  const str = identifier.toString();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Make hash positive
  const positiveHash = Math.abs(hash);

  // Define a set of visually distinct colors that work well with white borders
  const colors = [
    {
      bg: "#ef4444",
      hover: "#dc2626",
      bgClass: "bg-red-500",
      hoverClass: "hover:bg-red-600",
    }, // red
    {
      bg: "#3b82f6",
      hover: "#2563eb",
      bgClass: "bg-primary-500",
      hoverClass: "hover:bg-primary-600",
    }, // blue
    {
      bg: "#10b981",
      hover: "#059669",
      bgClass: "bg-emerald-500",
      hoverClass: "hover:bg-emerald-600",
    }, // emerald
    {
      bg: "#f59e0b",
      hover: "#d97706",
      bgClass: "bg-amber-500",
      hoverClass: "hover:bg-amber-600",
    }, // amber
    {
      bg: "#8b5cf6",
      hover: "#7c3aed",
      bgClass: "bg-violet-500",
      hoverClass: "hover:bg-violet-600",
    }, // violet
    {
      bg: "#06b6d4",
      hover: "#0891b2",
      bgClass: "bg-cyan-500",
      hoverClass: "hover:bg-cyan-600",
    }, // cyan
    {
      bg: "#f97316",
      hover: "#ea580c",
      bgClass: "bg-orange-500",
      hoverClass: "hover:bg-orange-600",
    }, // orange
    {
      bg: "#84cc16",
      hover: "#65a30d",
      bgClass: "bg-lime-500",
      hoverClass: "hover:bg-lime-600",
    }, // lime
    {
      bg: "#ec4899",
      hover: "#db2777",
      bgClass: "bg-pink-500",
      hoverClass: "hover:bg-pink-600",
    }, // pink
    {
      bg: "#14b8a6",
      hover: "#0d9488",
      bgClass: "bg-teal-500",
      hoverClass: "hover:bg-teal-600",
    }, // teal
    {
      bg: "#a855f7",
      hover: "#9333ea",
      bgClass: "bg-purple-500",
      hoverClass: "hover:bg-purple-600",
    }, // purple
    {
      bg: "#22c55e",
      hover: "#16a34a",
      bgClass: "bg-green-500",
      hoverClass: "hover:bg-green-600",
    }, // green
  ];

  // Select color based on hash
  const colorIndex = positiveHash % colors.length;
  return colors[colorIndex];
}
